import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { JsonKey, JsonKeyCode } from 'src/app/data/type/locale-copy-key';
import { getMoreInfoCopy } from 'src/app/utils/localization';
import { LocalizationService } from 'src/app/service/localization.service';
import { toggleTimeline } from '../../utils/toggleTimeline';
import gsap from 'gsap';
import { TvFeature, TvFeatureKey } from 'src/app/data/enum/tv-feature';
import { AnalyticsEvent, triggerEvent } from 'src/app/utils/analytics';
import { ParameterService } from 'src/app/service/parameter.service';
import { Parameter } from 'src/app/data/type/parameter';
import { isGenericAssistantButton } from 'src/app/utils/version';

interface ModalEvent {
  open: AnalyticsEvent;
  close: AnalyticsEvent;
}

interface LocalEvent {
  [TvFeature.BROWSE_TITLES]: ModalEvent;
  [TvFeature.RECOMMENDATIONS]: ModalEvent;
  [TvFeature.VOICE]: ModalEvent;
  [TvFeature.WATCHLIST]: ModalEvent;
}

const localEvents: LocalEvent = {
  [TvFeature.BROWSE_TITLES]: {
    open: AnalyticsEvent.browseTitlesMoreInfoOpen,
    close: AnalyticsEvent.browseTitlesMoreInfoClose,
  },
  [TvFeature.RECOMMENDATIONS]: {
    open: AnalyticsEvent.recommendationsMoreInfoOpen,
    close: AnalyticsEvent.recommendationsMoreInfoClose,
  },
  [TvFeature.VOICE]: {
    open: AnalyticsEvent.voiceMoreInfoOpen,
    close: AnalyticsEvent.voiceMoreInfoClose,
  },
  [TvFeature.WATCHLIST]: {
    open: AnalyticsEvent.watchlistMoreInfoOpen,
    close: AnalyticsEvent.watchlistMoreInfoClose,
  },
};

@Component({
  selector: 'app-c03-question-mark-modal',
  templateUrl: './c03-question-mark-modal.component.html',
  styleUrls: ['./c03-question-mark-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class C03QuestionMarkModalComponent implements OnInit, AfterViewInit {
  @ViewChild('modalContentRef') modalContentRef: ElementRef;
  @ViewChildren('questionModalButtonRef') questionModalButtonRef: Array<ElementRef>;
  @Input() featureName: TvFeatureKey;
  public readonly jsonKey = JsonKey;
  public isOpen: boolean = false;
  public modalTimeline: GSAPTimeline = gsap.timeline({ paused: true });
  public modalElement: HTMLDivElement | null;
  public featureMoreInfoTitleKey: JsonKeyCode;
  public featureMoreInfoMainCopyKey: JsonKeyCode;
  public featureMoreInfoLegalFooterKey: JsonKeyCode;
  private questionModalButtonElements: Array<HTMLElement> | null;

  public copy: Record<string, string>;

  constructor(
    private localizationService: LocalizationService,
    private parameterService: ParameterService,
  ) {}

  ngOnInit(): void {
    const brandName = this.parameterService.getParam(Parameter['brand']);
    const isGeneric = isGenericAssistantButton(brandName);

    this.featureMoreInfoTitleKey = `${this.featureName}_MORE_INFO_TITLE`;
    this.featureMoreInfoMainCopyKey = `${this.featureName}_MORE_INFO_MAIN_COPY`;
    this.featureMoreInfoLegalFooterKey = `${this.featureName}_MORE_INFO_LEGAL_FOOTER`;

    if (isGeneric && this.featureName === TvFeature.BROWSE_TITLES) {
      this.featureMoreInfoMainCopyKey = `${this.featureName}_MORE_INFO_GENERIC_COPY`;
      this.featureMoreInfoLegalFooterKey = `${this.featureName}_MORE_INFO_LEGAL_GENERIC_FOOTER`;
    }

    this.copy = getMoreInfoCopy(
      this.localizationService,
      this.featureMoreInfoTitleKey,
      this.featureMoreInfoMainCopyKey,
      this.featureMoreInfoLegalFooterKey,
    );
  }

  ngAfterViewInit(): void {
    this.questionModalButtonElements = this.questionModalButtonRef.map(
      (quiestionButton) => quiestionButton.nativeElement,
    );
    this.modalElement = this.modalContentRef.nativeElement;
    this.setupTimeline();
  }

  setupTimeline() {
    if (this.modalTimeline) {
      this.modalTimeline
        .to(this.modalElement, { y: 0, autoAlpha: 1, duration: 1.5, ease: 'power4.out' }, 0)
        .fromTo(
          '[data-animation="c03-modal-content"]',
          { autoAlpha: 0, y: 50, ease: 'sine.out' },
          { autoAlpha: 1, y: 0, stagger: 0.1, duration: 1, ease: 'sine.out' },
          0.75,
        );
    }
  }

  public onButtonMenuClick(event: Event) {
    const target = event.currentTarget as HTMLButtonElement;
    if (!gsap.isTweening(this.modalTimeline)) {
      this.isOpen = !this.isOpen;
      toggleTimeline(this.isOpen, this.modalTimeline, () => this.focusButtons(target));

      if (this.isOpen) {
        triggerEvent(localEvents[this.featureName].open);
      } else {
        triggerEvent(localEvents[this.featureName].close);
      }
    }
  }

  focusButtons(target: HTMLElement): void {
    this.questionModalButtonElements.filter((domElement) => domElement !== target)[0].focus();
  }

  // @HostListener('document:keydown', ['$event'])

  keyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.onButtonMenuClick(event);
    }
  }
}
